<div class="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm" (click)="onClose()">
  <div
    class="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-xl bg-white p-8 shadow-2xl"
    (click)="$event.stopPropagation()">
    <div class="mb-4 flex items-center justify-between">
      <h2 class="text-xl font-bold text-gray-900" i18n="@@adminApprovePharmacyTitle">Accept This Pharmacy Request?</h2>
      <button (click)="onClose()" class="text-gray-400 transition-colors hover:text-gray-600">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2.5"
          stroke-linecap="round"
          stroke-linejoin="round">
          <line x1="18" y1="6" x2="6" y2="18"></line>
          <line x1="6" y1="6" x2="18" y2="18"></line>
        </svg>
      </button>
    </div>

    <p class="mb-6 text-sm font-medium text-gray-500" i18n="@@adminApprovePharmacySub">
      Please Choose Pharmacy Segment & Address
    </p>

    <div class="space-y-5">
      <div *hasScope="SCOPES.PHARMACIES.READ_ALL_SEGMENTS" class="flex flex-col gap-1.5">
        <label class="text-xs font-bold text-gray-700">
          <span i18n="@@adminSegmentLabel">Pharmacy Segment</span>
          <span class="text-red-500">*</span>
        </label>
        <div class="relative flex items-center">
          <select
            [ngModel]="selectedSegmentId()"
            (ngModelChange)="selectedSegmentId.set($event)"
            class="focus-border-primary w-full appearance-none rounded-lg border border-gray-300 p-2.5 px-4 text-sm outline-none"
            [class.border-red-500]="showErrors() && !selectedSegmentId()"
            [class.text-gray-muted]="selectedSegmentId() === null">
            <option [ngValue]="null" disabled class="text-gray-muted" i18n="@@adminSelectSegment">
              Select pharmacy segment
            </option>
            @for (s of segments(); track s.id) {
              <option [value]="s.id" class="text-gray-900">{{ getSegmentDisplay(s) }}</option>
            }
          </select>
          <div class="pointer-events-none absolute flex items-center text-gray-500 ltr:right-3 rtl:left-3">
            <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
            </svg>
          </div>
        </div>
        @if (showErrors() && !selectedSegmentId()) {
          <span class="text-[10px] text-red-600">{{ getErrors().segment }}</span>
        }
      </div>

      <div class="flex flex-col gap-1.5">
        <label class="text-xs font-bold text-gray-700">
          <span i18n="@@adminQuotaLabel">Pharmacy Quota</span>
          <span class="text-red-500">*</span>
        </label>
        <input
          type="number"
          [ngModel]="pharmacyQuota()"
          (ngModelChange)="pharmacyQuota.set($event)"
          class="focus-border-primary placeholder-text-gray-muted w-full rounded-lg border border-gray-300 p-2.5 text-sm outline-none"
          [class.border-red-500]="showErrors() && getQuotaError()"
          placeholder="Enter pharmacy quota"
          i18n-placeholder="@@adminQuotaPlaceholder" />
        @if (showErrors() && getQuotaError()) {
          <p class="text-[10px] text-red-600">{{ getQuotaError() }}</p>
        }
        @if (getQuotaWarning()) {
          <p class="text-[10px] font-medium text-orange-600">{{ getQuotaWarning() }}</p>
        }
      </div>

      <div class="grid grid-cols-2 gap-4">
        <div *hasScope="SCOPES.PHARMACIES.READ_ALL_GOVERNORATES" class="flex flex-col gap-1.5">
          <label class="text-xs font-bold text-gray-700">
            <span i18n="@@adminGovLabel">Governorate</span>
            <span class="text-red-500">*</span>
          </label>
          <div class="relative flex items-center">
            <select
              [ngModel]="selectedGovernorateId()"
              (ngModelChange)="onGovernorateChange($event)"
              class="focus-border-primary w-full appearance-none rounded-lg border border-gray-300 p-2.5 px-4 text-sm outline-none"
              [class.border-red-500]="showErrors() && !selectedGovernorateId()"
              [class.text-gray-muted]="selectedGovernorateId() === null">
              <option [ngValue]="null" disabled class="text-gray-muted" i18n="@@adminSelectGov">
                Select Governorate
              </option>
              @for (g of governorates(); track g.id) {
                <option [value]="g.id" class="text-gray-900">{{ getLang(g) }}</option>
              }
            </select>
            <div class="pointer-events-none absolute flex items-center text-gray-500 ltr:right-3 rtl:left-3">
              <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
              </svg>
            </div>
          </div>
          @if (showErrors() && !selectedGovernorateId()) {
            <p class="text-[10px] text-red-600">{{ getErrors().gov }}</p>
          }
        </div>

        <div *hasScope="SCOPES.PHARMACIES.READ_ALL_CITIES" class="flex flex-col gap-1.5">
          <label class="text-xs font-bold text-gray-700">
            <span i18n="@@adminCityLabel">City</span>
            <span class="text-red-500">*</span>
          </label>
          <div class="relative flex items-center">
            <select
              [ngModel]="selectedCityId()"
              (ngModelChange)="onCityChange($event)"
              [disabled]="!selectedGovernorateId()"
              class="focus-border-primary w-full appearance-none rounded-lg border border-gray-300 p-2.5 px-4 text-sm outline-none disabled:bg-gray-50"
              [class.border-red-500]="showErrors() && !selectedCityId()"
              [class.text-gray-muted]="selectedCityId() === null">
              <option [ngValue]="null" disabled class="text-gray-muted" i18n="@@adminSelectCity">Select City</option>
              @for (c of cities(); track c.id) {
                <option [value]="c.id" class="text-gray-900">{{ getLang(c) }}</option>
              }
            </select>
            <div class="pointer-events-none absolute flex items-center text-gray-500 ltr:right-3 rtl:left-3">
              <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
              </svg>
            </div>
          </div>
          @if (showErrors() && !selectedCityId()) {
            <p class="text-[10px] text-red-600">{{ getErrors().city }}</p>
          }
        </div>
      </div>

      <div *hasScope="SCOPES.PHARMACIES.READ_ALL_REGIONS" class="flex flex-col gap-1.5">
        <label class="text-xs font-bold text-gray-700">
          <span i18n="@@adminRegionLabel">Region</span>
          <span class="text-red-500">*</span>
        </label>
        <div class="relative flex items-center">
          <select
            [ngModel]="selectedRegionId()"
            (ngModelChange)="selectedRegionId.set($event)"
            [disabled]="!selectedCityId()"
            class="focus-border-primary w-full appearance-none rounded-lg border border-gray-300 p-2.5 px-4 text-sm outline-none disabled:bg-gray-50"
            [class.border-red-500]="showErrors() && !selectedRegionId()"
            [class.text-gray-muted]="selectedRegionId() === null">
            <option [ngValue]="null" disabled class="text-gray-muted" i18n="@@adminSelectRegion">Select Region</option>
            @for (r of regions(); track r.id) {
              <option [value]="r.id" class="text-gray-900">{{ getLang(r) }}</option>
            }
          </select>
          <div class="pointer-events-none absolute flex items-center text-gray-500 ltr:right-3 rtl:left-3">
            <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
            </svg>
          </div>
        </div>
        @if (showErrors() && !selectedRegionId()) {
          <p class="text-[10px] text-red-600">{{ getErrors().region }}</p>
        }
      </div>

      <app-assign-representative
        [pharmacyForm]="representativeForm"
        [showRepresentativeDropdown]="showRepresentativeDropdown()"
        [representativeResults]="representativeResults()"
        (selectRepresentative)="onSelectRepresentative($event)"
        [loadingRepresentative]="loadingRepresentative()"
        [errorRepresentative]="errorRepresentative()" />

      <div class="flex flex-col gap-1.5">
        <label class="text-xs font-bold text-gray-700" i18n="@@adminCommentLabel">Comment</label>
        <textarea
          [formControl]="commentControl"
          rows="4"
          class="focus-border-primary placeholder-text-gray-muted w-full resize-none rounded-lg border border-gray-300 p-3 text-sm outline-none"
          [class.border-red-500]="commentControl.invalid && commentControl.touched"
          placeholder="Write your comment here"
          i18n-placeholder="@@adminCommentPlaceholder"></textarea>
        @if (commentControl.errors?.["maxlength"] && commentControl.touched) {
          <p class="text-[10px] text-red-600">
            {{ isArabic() ? "التعليق لا يمكن أن يتجاوز 2000 حرف" : "Comment cannot exceed 2000 characters" }}
          </p>
        }
      </div>

      @if (backendError()) {
        <p class="rounded-md border border-red-100 bg-red-50 p-2.5 text-xs font-medium text-red-600">
          {{ backendError() }}
        </p>
      }
    </div>

    <div class="mt-8 flex justify-end gap-3">
      <button
        *hasScope="SCOPES.PHARMACIES.APPROVE"
        type="button"
        (click)="onConfirm()"
        [disabled]="isSubmitting()"
        class="bg-primary hover-bg-primary-hover flex min-w-[100px] cursor-pointer items-center justify-center gap-2 rounded-lg px-4 py-2 text-sm font-bold text-white transition-colors disabled:opacity-50 ltr:order-2 rtl:order-1">
        @if (isSubmitting()) {
          <svg
            class="h-4 w-4 animate-spin text-white"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
          </svg>
          <span i18n="@@adminApproveSaving">Saving...</span>
        } @else {
          <span i18n="@@adminApproveSave">Save</span>
        }
      </button>

      <button
        type="button"
        (click)="onClose()"
        class="border-primary text-primary hover-bg-primary-10 min-w-[100px] cursor-pointer rounded-lg border px-4 py-2 text-sm font-bold transition-colors ltr:order-1 rtl:order-2"
        i18n="@@adminApproveCancel">
        Cancel
      </button>
    </div>
  </div>
</div>
